import React, { useMemo, useState, useEffect } from "react";
import { Check, Shield, FileText, Search, Clock, HelpCircle, Info, Layers, ClipboardList, BookOpen, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Card, CardContent } from '../ui/card';
import { API_URLS } from "../../config/api";
import MediaDisplay from '../MediaDisplay';
import { formatPrice } from '../../utils/format';

export default function MarkaTescilPage() {
  const [brandName, setBrandName] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [allClasses, setAllClasses] = useState([]); // Store all classes from API
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdditionalServiceModalOpen, setIsAdditionalServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [applicationType, setApplicationType] = useState('bireysel');
  const [individualForm, setIndividualForm] = useState({
    fullName: '',
    nationalId: '',
    address: '',
    country: '',
    city: '',
    district: '',
    postalCode: '',
    phone: '',
    fax: ''
  });
  const [corporateForm, setCorporateForm] = useState({
    companyName: '',
    taxOffice: '',
    taxNumber: '',
    address: '',
    country: '',
    city: '',
    district: '',
    postalCode: '',
    phone: '',
    fax: ''
  });
  const [additionalServiceForm, setAdditionalServiceForm] = useState({
    markaAdi: '',
    requiredDocuments: []
  });
  const [blogs, setBlogs] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Simple pricing model per class (adjust as needed)
  const PER_CLASS_HARC = 480.0;
  const PER_CLASS_SERVICE = 1490.0;

  const totals = useMemo(() => {
    const count = selectedClasses.length;
    const harc = count * PER_CLASS_HARC;
    const service = count * PER_CLASS_SERVICE;
    const total = harc + service;
    return { count, harc, service, total };
  }, [selectedClasses]);

  const fetchMarkaTescilClasses = async () => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getMarkaTescilClasses`);
      const data = await response.json();
      if (data.status === 'success' && Array.isArray(data.data)) {
        setAllClasses(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch marka tescil classes:', error);
    }
  };

  const fetchAdditionalServices = async () => {
    try {
      const response = await fetch(API_URLS.MARKA_TESCIL_ADDITIONAL_SERVICES);
      const data = await response.json();
      if (data.status === 'success') {
        // Filter only active services
        const activeServices = data.data.filter(service => service.status === 'active');
        setAdditionalServices(activeServices);
      }
    } catch (error) {
      console.error('Failed to fetch additional services:', error);
    }
  };

  const fetchBlogs = async () => {
    try {
      setLoadingBlogs(true);
      const response = await fetch(`${API_URLS.BLOGS}&category=marka-tescil&limit=3`);
      const data = await response.json();
      const blogsArray = Array.isArray(data) ? data : (data.blogs || data.data || []);
      
      // Fallback: fetch all and filter if empty
      if (!blogsArray || blogsArray.length === 0) {
        const allResponse = await fetch(API_URLS.BLOGS);
        const allData = await allResponse.json();
        const allBlogs = Array.isArray(allData) ? allData : (allData.blogs || allData.data || []);
        const markaTescilBlogs = allBlogs.filter(blog => 
          blog.category && (
            blog.category.toLowerCase().includes('marka-tescil') ||
            blog.category.toLowerCase().includes('marka tescil') ||
            (blog.category_name && blog.category_name.toLowerCase().includes('marka tescil'))
          )
        ).slice(0, 3);
        setBlogs(markaTescilBlogs);
      } else {
        setBlogs(blogsArray);
      }
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
    } finally {
      setLoadingBlogs(false);
    }
  };

  const fetchFaqs = async () => {
    try {
      setLoadingFaqs(true);
      const response = await fetch(API_URLS.FAQ);
      const data = await response.json();
      if (data.questions) {
        // Find Marka Tescil category ID
        const markaTescilCategory = data.categories.find(cat => 
          cat.title && cat.title.toLowerCase().includes('marka tescil')
        );
        
        if (markaTescilCategory) {
          // Filter FAQs by Marka Tescil category ID
          const markaTescilFaqs = data.questions.filter(faq => 
            faq.category_id === markaTescilCategory.id
          );
          setFaqs(markaTescilFaqs);
        } else {
          setFaqs([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoadingFaqs(false);
    }
  };

  const handleTrademarkSearch = () => {
    if (!brandName.trim()) {
      alert('Lütfen bir marka adı girin');
      return;
    }

    // Türk Patent resmi sitesine yönlendir
    const searchParams = {
      markTypeId: "0",
      searchText: brandName.trim(),
      searchTextOption: "isContains",
      holderName: "",
      holderNameOption: "isStartWith",
      niceClassesFor: "all"
    };

    const searchUrl = `https://www.turkpatent.gov.tr/arastirma-yap?form=trademark&params=${encodeURIComponent(JSON.stringify(searchParams))}&run=true`;
    
    // Yeni sekmede aç
    window.open(searchUrl, '_blank');
  };

  const toggleClass = (label) => {
    setSelectedClasses((prev) => {
      const set = new Set(prev);
      if (set.has(label)) {
        set.delete(label);
      } else {
        set.add(label);
      }
      return Array.from(set);
    });
  };

  const formatTRY = (n) => n.toLocaleString("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });

  useEffect(() => {
    fetchMarkaTescilClasses();
    fetchAdditionalServices();
    fetchBlogs();
    fetchFaqs();
  }, []);

  // Fetch page content from DB
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getPageContent&slug=marka-tescil-sorgulama`);
        const txt = await res.text();
        let json; try { json = JSON.parse(txt); } catch { json = {}; }
        if (json?.status === 'success' && json?.data) {
          setPageContent(json.data);
        }
      } catch (e) {
        // fallback remains
      }
    })();
  }, []);

  const additional = useMemo(() => pageContent?.additional_sections || {}, [pageContent]);
  const hero = additional?.hero || {};
  const heroTitle = pageContent?.hero_title || 'Marka Tescil';
  const heroSubtitle = pageContent?.hero_badge_text || 'Marka Tescil Başvurusu';
  const heroDesc = pageContent?.hero_description || 'Marka tescil başvurunuzu hemen yapın, profesyonel uzman desteği ile markanızı 10 yıl boyunca yasal koruma altına alın.';
  const heroImage = hero?.image_url || 'https://cdn.natrocdn.com/ResourceFiles/v1/images/MarkaTescil/v2/Reg-1.png';
  const whatIsSection = additional?.what_is_trademark_section || {};
  const searchSection = additional?.trademark_search_section || {};
  const searchBullets = Array.isArray(searchSection.bullets) ? searchSection.bullets : [];
  const processSection = additional?.application_process_section || {};
  const processSteps = Array.isArray(processSection.steps) ? processSection.steps : [];
  const advantagesSection = additional?.advantages_section || {};
  const advantageCards = Array.isArray(advantagesSection.cards) ? advantagesSection.cards : [];
  const ctaSection = additional?.cta_section || {};

  return (
    <div className="bg-white">
      {/* Hero */}
      <section 
        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl font-extrabold mb-3 tracking-tight">{heroTitle}</h1>
              <h2 className="text-xl sm:text-2xl font-bold mb-4">{heroSubtitle}</h2>
              <p className="text-white/90 mb-6">{heroDesc}</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 justify-center lg:justify-start"><Check className="w-5 h-5" /> Logo ve Marka Tescili</li>
                <li className="flex items-center gap-2 justify-center lg:justify-start"><Check className="w-5 h-5" /> Sınıf Seçimi ve Danışmanlık</li>
                <li className="flex items-center gap-2 justify-center lg:justify-start"><Check className="w-5 h-5" /> 10 Yıl Yasal Koruma</li>
              </ul>
            </div>
            <div className="hidden lg:flex justify-end">
              <MediaDisplay
                src={heroImage}
                alt="Marka Tescil"
                className="max-w-full h-auto rounded-lg shadow-lg"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marka Tescil Sorgulama - DB içeriği */}
      <section 
        className="py-12 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{searchSection.title || 'Marka Tescil Sorgulama'}</h3>
              <p className="text-gray-600 mb-4">{searchSection.subtitle || ''}</p>
              <ul className="space-y-2 mx-auto lg:mx-0 max-w-md lg:max-w-none">
                {searchBullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-700 justify-center lg:justify-start">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    {b.text || ''}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Başvuru ve Takip Süreci - DB içeriği */}
      <section 
        className="py-12 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center lg:text-left">{processSection.title || 'Başvuru ve Takip Süreci'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {processSteps.map((s, i) => (
              <div key={i} className="rounded-lg border border-gray-200 p-4 text-center lg:text-left">
                <div className="font-semibold text-gray-900">{s.title || ''}</div>
                <div className="text-sm text-gray-600">{s.description || ''}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marka Tescili Avantajları - DB içeriği */}
      <section 
        className="py-12 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center lg:text-left">{advantagesSection.title || 'Marka Tescili Avantajları'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantageCards.map((card, i) => (
              <div key={i} className="border rounded-2xl p-6 bg-white text-center lg:text-left">
                <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center mb-3 mx-auto lg:mx-0">
                  <Shield className="w-6 h-6" />
                </div>
                <div className="font-semibold text-gray-900 mb-1">{card.title || ''}</div>
                <div className="text-sm text-gray-600">{card.description || ''}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - DB içeriği */}
      <section 
        className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h3 className="text-3xl font-bold mb-3">{ctaSection.title || 'Başlamaya Hazır mısın?'}</h3>
            <p className="text-blue-100 mb-6">{ctaSection.description || ''}</p>
            <a href={ctaSection.button_link || '/iletisim'} className="inline-flex items-center px-6 h-12 rounded-lg bg-white text-blue-600 hover:bg-gray-100 font-semibold">
              {ctaSection.button_text || 'Uzmanla Görüş'}
            </a>
          </div>
        </div>
      </section>
      

      {/* Marka Adı Arama Alanı */}
      <section 
        className="py-12 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 text-center lg:text-left">
              <h3 className="text-2xl font-bold mb-2">Marka Adı</h3>
              <p className="text-gray-600 mb-4">Lütfen marka tescili sorgulama işlemi yapmak istediğiniz marka adını yazın.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Marka adınızı yazın (örn. Website Al)"
                  className="flex-1 border border-gray-200 rounded-lg px-4 h-12 leading-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button 
                  onClick={handleTrademarkSearch}
                  className="h-12 px-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2"
                >
                  <Search className="w-5 h-5" /> 
                  Sorgula
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Bu alan yalnızca bilgilendirme amaçlıdır.</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-center lg:text-left">
              <div className="flex items-start gap-3 justify-center lg:justify-start">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900">Marka adınızın tescile uygunluğunu hızlıca kontrol edin, detaylı ön araştırma için danışmanlarımızla iletişime geçin.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Marka Tescil Sınıfları */}
      <section 
        className="py-12"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-2 justify-center lg:justify-start">
            <Layers className="w-5 h-5 text-blue-600" />
            <h3 className="text-2xl font-bold">Marka Tescil Sınıfları</h3>
          </div>
          <p className="text-gray-600 mb-6 text-center lg:text-left">Lütfen tescil etmek istediğiniz marka tescil sınıf(lar)ını seçin</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              '01 - Kimyasallar',
              '02 - Boyalar',
              '03 - Parfümeri, Kozmetik Ürünler',
              '04 - Sinai Amaçlı Yağlar',
              '05 - İlaçlar',
              '06 - Adi Metaller',
              '07 - Motorlar ve Makineler',
              '08 - Çatal, Bıçak, Kaşık',
              '09 - Manyetik, Optik Kayıt Taşıcıyılar',
              '10 - Cerrahi, Tıbbi Cihazlar',
              '11 - Aydınlatma Cihazları',
              '12 - Taşıtlar',
              '13 - Ateşli Silahlar',
              '14 - Kuyumculuk Eşyaları',
              '15 - Müzik Enstrümanları',
              '16 - Basılı Yayınlar',
              '17 - Yalıtım, Dolgu Malzemeleri',
              '18 - İşlenmiş, İşlenmemiş Deriler',
              '19 - İnşaat Malzemeleri',
              '20 - Mobilyalar',
              '21 - Elektriksiz Mutfak Aletleri',
              '22 - Sentetik Elyaflar, Yün',
              '23 - İplikler',
              '24 - Ev Tekstil Ürünleri',
              '25 - İç/Dış Giysiler',
              '26 - Danteller ve Nakışlar',
              '27 - Halılar, Kilimler, Yolluklar',
              '28 - Oyunlar ve Oyuncaklar',
              '29 - İşlenmiş Et Ürünleri',
              '30 - Pasta ve Fırıncılık Mamülleri',
              '31 - Tarım ve Bahçecilik Ürünleri',
              '32 - İçecekler',
              '33 - Alkollü İçecekler',
              '34 - Tütün, Çiğneme Tütünleri, Sigaralar',
              '35 - Reklamcılık, Pazarlama ve Satış',
              '36 - Finansal ve Parasal Hizmetler',
              '37 - İnşaat Hizmetleri',
              '38 - Telekomünikasyon Hizmetleri',
              '39 - Taşımacılık Hizmetleri',
              '40 - Malzemelerin İşlenmesi Hizmetleri',
              '41 - Eğitim ve Öğretim Hizmetleri',
              '42 - Mühendislik, Mimarlık, Bilgisayar',
              '43 - Yiyecek, İçecek, Konaklama',
              '44 - Tıbbi Hizmetler',
              '45 - Hukuki Hizmetler'
            ].map((label, idx) => {
              const selected = selectedClasses.includes(label);
              return (
                <label
                  key={idx}
                  className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-400'}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleClass(label)}
                    className="w-4 h-4"
                  />
                  <span className={`text-sm ${selected ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>{label}</span>
                </label>
              );
            })}
          </div>
          <div className="mt-6">
            <div className="rounded-xl border border-gray-100 p-4 bg-white">
              <div className="font-semibold mb-3">Seçtiğiniz Sınıflara Göre Harç ve Hizmet Bedeli</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Kurum Harcı</span><span className="font-medium">{formatTRY(totals.harc)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Hizmet Bedeli</span><span className="font-medium">{formatTRY(totals.service)}</span></div>
                <div className="pt-2 mt-2 border-t flex justify-between"><span className="text-gray-900 font-semibold">Toplam</span><span className="text-gray-900 font-bold">{formatTRY(totals.total)}</span></div>
              </div>
              <button
                className="mt-4 w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                Başvuru Yap
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Marka Tescili Nedir? */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center flex-col-reverse lg:grid">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg overflow-hidden">
              <MediaDisplay
                src={whatIsSection.image_url || "https://cdn.natrocdn.com/ResourceFiles/v1/images/MarkaTescil/v2/Reg-2.png"}
                alt="Marka Tescili Nedir?"
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{whatIsSection.title || 'Marka Tescili Nedir?'}</h3>
              <div>
                <p className="text-gray-700 leading-relaxed">{whatIsSection.description || ''}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Başvuru Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsModalOpen(false)} />
          <div className="relative bg-white w-full max-w-3xl rounded-xl shadow-xl border border-gray-100 mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <div className="text-lg font-bold">Marka Tescil Başvurusu</div>
                <div className="text-sm text-gray-600">Marka: <span className="font-medium text-gray-900">{brandName || '-'}</span> • Sınıf: <span className="font-medium text-gray-900">{totals.count}</span> • Toplam: <span className="font-semibold text-gray-900">{formatTRY(totals.total)}</span></div>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setIsModalOpen(false)}>✕</button>
            </div>

            <div className="px-6 py-4">
              {/* Tabs */}
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden mb-6">
                <button
                  className={`px-4 h-10 text-sm font-medium ${applicationType === 'bireysel' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                  onClick={() => setApplicationType('bireysel')}
                >
                  Bireysel Başvuru
                </button>
                <button
                  className={`px-4 h-10 text-sm font-medium border-l border-gray-200 ${applicationType === 'kurumsal' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                  onClick={() => setApplicationType('kurumsal')}
                >
                  Kurumsal Başvuru
                </button>
              </div>

              {/* Forms */}
              {applicationType === 'bireysel' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-700">Ad Soyad</label>
                    <input value={individualForm.fullName} onChange={(e) => setIndividualForm({ ...individualForm, fullName: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">TC Kimlik No</label>
                    <input value={individualForm.nationalId} onChange={(e) => setIndividualForm({ ...individualForm, nationalId: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Telefon</label>
                    <input value={individualForm.phone} onChange={(e) => setIndividualForm({ ...individualForm, phone: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Ülke</label>
                    <input value={individualForm.country} onChange={(e) => setIndividualForm({ ...individualForm, country: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Şehir</label>
                    <input value={individualForm.city} onChange={(e) => setIndividualForm({ ...individualForm, city: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">İlçe</label>
                    <input value={individualForm.district} onChange={(e) => setIndividualForm({ ...individualForm, district: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Posta Kodu</label>
                    <input value={individualForm.postalCode} onChange={(e) => setIndividualForm({ ...individualForm, postalCode: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Faks</label>
                    <input value={individualForm.fax} onChange={(e) => setIndividualForm({ ...individualForm, fax: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-700">Adres</label>
                    <textarea value={individualForm.address} onChange={(e) => setIndividualForm({ ...individualForm, address: e.target.value })} className="mt-1 w-full min-h-[88px] border border-gray-200 rounded-lg px-3 py-2" />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-700">Şirket Unvanı</label>
                    <input value={corporateForm.companyName} onChange={(e) => setCorporateForm({ ...corporateForm, companyName: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Vergi Dairesi</label>
                    <input value={corporateForm.taxOffice} onChange={(e) => setCorporateForm({ ...corporateForm, taxOffice: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Vergi No</label>
                    <input value={corporateForm.taxNumber} onChange={(e) => setCorporateForm({ ...corporateForm, taxNumber: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Telefon</label>
                    <input value={corporateForm.phone} onChange={(e) => setCorporateForm({ ...corporateForm, phone: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Ülke</label>
                    <input value={corporateForm.country} onChange={(e) => setCorporateForm({ ...corporateForm, country: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Şehir</label>
                    <input value={corporateForm.city} onChange={(e) => setCorporateForm({ ...corporateForm, city: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">İlçe</label>
                    <input value={corporateForm.district} onChange={(e) => setCorporateForm({ ...corporateForm, district: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Posta Kodu</label>
                    <input value={corporateForm.postalCode} onChange={(e) => setCorporateForm({ ...corporateForm, postalCode: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Faks</label>
                    <input value={corporateForm.fax} onChange={(e) => setCorporateForm({ ...corporateForm, fax: e.target.value })} className="mt-1 w-full h-11 border border-gray-200 rounded-lg px-3" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-700">Adres</label>
                    <textarea value={corporateForm.address} onChange={(e) => setCorporateForm({ ...corporateForm, address: e.target.value })} className="mt-1 w-full min-h-[88px] border border-gray-200 rounded-lg px-3 py-2" />
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
              <button className="h-10 px-4 rounded-md border border-gray-200" onClick={() => setIsModalOpen(false)}>İptal</button>
              <button
                className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium"
                onClick={async () => {
                  try {
                    // Map individual form fields to backend expected format
                    const individualData = applicationType === 'bireysel' ? {
                      ad_soyad: individualForm.fullName,
                      tc_kimlik_no: individualForm.nationalId,
                      adres: individualForm.address,
                      ulke: individualForm.country,
                      sehir: individualForm.city,
                      ilce: individualForm.district,
                      posta_kodu: individualForm.postalCode,
                      telefon: individualForm.phone,
                      faks: individualForm.fax
                    } : {};

                    // Map corporate form fields to backend expected format
                    const corporateData = applicationType === 'kurumsal' ? {
                      sirket_unvani: corporateForm.companyName,
                      vergi_dairesi: corporateForm.taxOffice,
                      vergi_no: corporateForm.taxNumber,
                      adres: corporateForm.address,
                      ulke: corporateForm.country,
                      sehir: corporateForm.city,
                      ilce: corporateForm.district,
                      posta_kodu: corporateForm.postalCode,
                      telefon: corporateForm.phone,
                      faks: corporateForm.fax
                    } : {};

                    // Map selected class labels to full class objects with code, name, and prices
                    const mappedSelectedClasses = selectedClasses.map(selectedLabel => {
                      // Find the class in allClasses that matches the label
                      const matchedClass = allClasses.find(cls => cls.name === selectedLabel);
                      if (matchedClass) {
                        return {
                          code: matchedClass.code,
                          name: matchedClass.name.replace(/^\d+\s*-\s*/, ''), // Remove "1 - " prefix if exists
                          kurum_harc: matchedClass.kurum_harc || 480.00,
                          hizmet_bedeli: matchedClass.hizmet_bedeli || 1490.00
                        };
                      }
                      // Fallback: Parse label manually if not found in API
                      const match = selectedLabel.match(/^(\d+)\s*-\s*(.+)$/);
                      return {
                        code: match ? match[1] : selectedLabel.split(' - ')[0],
                        name: match ? match[2] : selectedLabel.split(' - ').slice(1).join(' - '),
                        kurum_harc: 480.00,
                        hizmet_bedeli: 1490.00
                      };
                    });

                    const payload = {
                      marka_adi: brandName,
                      application_type: applicationType,
                      selected_classes: mappedSelectedClasses,
                      total_amount: totals.total,
                      individual_data: individualData,
                      corporate_data: corporateData
                    };
                    
                    const response = await fetch('http://localhost:8000/backend/admin_api.php?action=createMarkaTescilApplication', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload)
                    });
                    
                    const result = await response.json();
                    if (result.status === 'success') {
                      alert('Başvurunuz başarıyla gönderildi!');
                      setIsModalOpen(false);
                      // Reset form
                      setBrandName('');
                      setSelectedClasses([]);
                      setIndividualForm({ fullName: '', nationalId: '', address: '', country: '', city: '', district: '', postalCode: '', phone: '', fax: '' });
                      setCorporateForm({ companyName: '', taxOffice: '', taxNumber: '', address: '', country: '', city: '', district: '', postalCode: '', phone: '', fax: '' });
                    } else {
                      alert('Başvuru gönderilirken hata oluştu: ' + (result.message || 'Bilinmeyen hata'));
                    }
                  } catch (error) {
                    alert('Başvuru gönderilirken hata oluştu: ' + error.message);
                  }
                }}
              >
                Gönder
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Süreç */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-2xl font-bold text-center mb-2">Marka Tescil Sorgulama, Başvuru ve Takip Süreci</h3>
          <p className="text-center text-gray-600 mb-10">Online marka tescil sorgulama ile yalnızca 3 adımda kolay başvuru</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-gray-100 rounded-xl p-6 bg-white text-center lg:text-left">
              <Search className="w-10 h-10 text-blue-600 mb-3 mx-auto lg:mx-0" />
              <h4 className="font-semibold mb-1">1) Ücretsiz Ön Araştırma</h4>
              <p className="text-gray-600">Markanızın tescile uygunluğunu kontrol edin, sınıfları belirleyin.</p>
            </div>
            <div className="border border-gray-100 rounded-xl p-6 bg-white text-center lg:text-left">
              <FileText className="w-10 h-10 text-blue-600 mb-3 mx-auto lg:mx-0" />
              <h4 className="font-semibold mb-1">2) Başvuru ve Evraklar</h4>
              <p className="text-gray-600">Gerekli belgeleri hazırlayarak TÜRKPATENT'e başvurunuzu yapın.</p>
            </div>
            <div className="border border-gray-100 rounded-xl p-6 bg-white text-center lg:text-left">
              <Shield className="w-10 h-10 text-blue-600 mb-3 mx-auto lg:mx-0" />
              <h4 className="font-semibold mb-1">3) Yayın ve Tescil</h4>
              <p className="text-gray-600">İnceleme ve bülten sürecinin ardından tescil belgenizi alın.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Avantajlar */}
      <section 
        className="py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Marka Tescili Avantajları</h3>
              <p className="text-gray-600 mb-8">Marka Tescil Başvurusunu Natro Üzerinden Yapmanın Sağladığı Avantajlar!</p>
              <div className="space-y-4">
                {[
                  {
                    title: 'Yasal Koruma',
                    desc: 'Marka tescil işlemi yaptığınız markanız, 6769 nolu Sinai mülkiyet kanunu kapsamında her türlü taklit, izinsiz kullanım ve iltibas tehlikesine karşı yasal olarak koruma altına alınmış olur.',
                    Icon: Shield
                  },
                  {
                    title: 'E-Ticaret Güvencesi',
                    desc: 'E-ticaret siteleri ve Pazar yeri platformlarında markanız için koruma sağlar, farklı satıcıların markanızın altında listelenerek aynı ürünü satmasını önler.',
                    Icon: Shield
                  },
                  {
                    title: 'Marka Tescil Belgesi',
                    desc: 'Marka tescil işlemi sonucunda, markanıza özel marka tescil belgesi düzenlenir. Markanızın ismi ve şeklinin yer aldığı marka tescil belgesi ile 10 yıl boyunca koruma altında olursunuz.',
                    Icon: FileText
                  },
                  {
                    title: 'Yasal Hak Sahipliği',
                    desc: 'Tescilli bir markanın başkası tarafından izinsiz kullanıması halinde, markanın gerçek hak sahibinin itiraz etme veya dava açarak yasal işlem uygulatma hakkı bulunmaktadır.',
                    Icon: Check
                  },
                  {
                    title: '10 Yıl Marka Koruması',
                    desc: 'Başvuru esnasında yapacağınız marka tescil ücreti ödemesi ile markanız, seçtiğiniz ve tescil edilmeye uygun mark tescil sınıfları üzerinde 10 yıl boyunca koruma altına alınır.',
                    Icon: Clock
                  },
                  {
                    title: 'Profesyonel Danışmanlık',
                    desc: 'Profesyonel marka uzmanlarımız tarafından tüm araştırma ve başvuru süreci sizin adınıza gerçekleştirilir. Markanız için gerekli tüm işlemler ve evrak süreçlerinde uzman desteği sunulur.',
                    Icon: FileText
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex flex-col lg:flex-row items-start gap-4 border border-gray-100 rounded-xl p-4 bg-white hover:shadow-lg transition-all">
                    <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 mx-auto lg:mx-0">
                      <item.Icon className="w-6 h-6" />
                    </div>
                    <div className="text-center lg:text-left flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg overflow-hidden">
              <MediaDisplay
                src={advantagesSection.image_url || "https://cdn.natrocdn.com/ResourceFiles/v1/images/MarkaTescil/v2/Reg-3.png"}
                alt="Marka Tescili Avantajları"
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* İçerikler */}
      <section 
        className="py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-2xl font-bold text-center mb-2">Marka Tescil ile İlgili Güncel İçerikler</h3>
          <p className="text-center text-gray-600 mb-10">Marka Tescil ve Başvuru Süreci ile İlgili İçerikler, Faydalı Araçlar</p>
          
          {loadingBlogs ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">İçerikler yükleniyor...</p>
            </div>
          ) : blogs.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {blogs.slice(0, 3).map((blog) => {
                const hasValidImage = blog.image && !blog.image.includes('placeholder');
                const excerpt = blog.excerpt || (() => {
                  const cleanContent = blog.content?.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').trim() || '';
                  return cleanContent.length > 100 ? cleanContent.substring(0, 100) + '...' : cleanContent;
                })();
                
                return (
                  <Card key={blog.id} className="overflow-hidden border-blue-100 hover:shadow-lg transition-shadow">
                    {hasValidImage ? (
                      <div className="aspect-[16/9] bg-blue-50 overflow-hidden">
                        <MediaDisplay
                          src={blog.image}
                          alt={blog.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          videoProps={{ controls: true }}
                          imgProps={{
                            loading: "lazy",
                            onError: (e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/9] bg-gradient-to-br from-blue-500 to-indigo-700 flex items-center justify-center">
                        <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                      </div>
                    )}
                    <CardContent className="p-5 space-y-3">
                      <div className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-600 text-xs font-semibold rounded-full">
                        <BookOpen className="w-3.5 h-3.5 mr-1" />
                        {blog.category_name || blog.category || 'Marka Tescil'}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{blog.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{excerpt}</p>
                      <button
                        onClick={() => window.location.href = `/blog/${blog.slug || blog.id}`}
                        className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700"
                      >
                        Yazıyı Oku
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">Bu kategori için içerik bulunamadı.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA - Başlamaya Hazır mısın? */}
      <section 
        className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-extrabold mb-3 tracking-tight">Başlamaya Hazır mısın?</h3>
                <p className="text-white/90">Marka adınızı girin, uygun sınıfları seçin ve başvurunuzu hemen başlatın. Uzman danışmanlarımız her adımda yanınızda.</p>
              </div>
              <div className="flex md:justify-end">
                <a href="#" className="inline-flex items-center h-12 px-6 rounded-lg bg-white text-blue-700 font-semibold hover:bg-gray-100">Hemen Başvur</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SSS */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Sıkça Sorulan <span className="text-blue-600">Sorular</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Marka Tescil ile ilgili merak ettiğiniz soruların cevaplarını burada bulabilirsiniz
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {loadingFaqs ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-500">Yükleniyor...</p>
              </div>
            ) : faqs.length > 0 ? (
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq) => (
                  <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                    <AccordionTrigger className="text-left">
                      <div className="flex items-center">
                        <HelpCircle className="w-5 h-5 mr-3 text-blue-600" />
                        {faq.question}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Bu kategori için soru bulunamadı.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Additional Service Modal */}
      {isAdditionalServiceModalOpen && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Markanız için ek hizmet satın alın!</h3>
              <button 
                onClick={() => setIsAdditionalServiceModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold text-blue-600 mb-2">{selectedService.title}</h4>
              <p className="text-gray-600 text-sm">{selectedService.desc}</p>
            </div>

            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marka Adı</label>
                <input 
                  type="text" 
                  value={additionalServiceForm.markaAdi}
                  onChange={(e) => setAdditionalServiceForm(prev => ({ ...prev, markaAdi: e.target.value }))}
                  placeholder="Marka adını girin"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {selectedService && selectedService.required_documents && selectedService.required_documents.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gerekli Belgeler</label>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {selectedService.required_documents.map((doc, idx) => (
                      <li key={idx}>{doc}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-semibold mb-3">Kurum Ücretleri</h5>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Kurum Ücretleri</span>
                    <span className="text-sm font-medium">{formatPrice(selectedService?.kurum_ucreti || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Servis Bedeli</span>
                    <span className="text-sm font-medium">{formatPrice(selectedService?.servis_bedeli || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Toplam</span>
                    <span className="font-semibold text-blue-600">
                      {formatPrice((parseFloat(selectedService?.kurum_ucreti || 0) + parseFloat(selectedService?.servis_bedeli || 0)))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button 
                  type="button"
                  onClick={() => setIsAdditionalServiceModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  İptal
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Başvuru Yap
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


